import {CreatorError} from './creatorDomain.ts';

export const WINDSOR_REPORT_CONTRACT = 'mirror_windsor_report_v1' as const;
export const WINDSOR_REPORT_LIMITS = {days: 31, fields: 8, rows: 100, providerRows: 5000,
  connectors: 400, accounts: 2000, catalogFields: 2000, metadataBytes: 800000, cellChars: 10000} as const;
export const WINDSOR_REPORT_ACTIONS = ['report_sources', 'report_schema', 'report'] as const;
export type WindsorReportAction = typeof WINDSOR_REPORT_ACTIONS[number];
export type WindsorScalar = string | number | boolean | null;
export type WindsorFieldType = 'TEXT' | 'NUMERIC' | 'PERCENT' | 'DATE' | 'TIMESTAMP' | 'BOOLEAN' | 'OBJECT';
export interface WindsorReportField {id: string; name: string; type: WindsorFieldType;
  table: string | null; description: string; reportable: boolean}
export interface WindsorReportSource {id: string; name: string; accounts: Array<{id: string; name: string | null}>}
export interface WindsorReportContext {owner_id: string; session_id: string; provider: 'windsor';
  connection_id: string; generation: string; creator_account_id: string}
export interface WindsorReportCoverage {state: 'unknown'; returned: number; limit: 100; display_partial: boolean}
export interface WindsorReportRequest {read_contract: typeof WINDSOR_REPORT_CONTRACT; expected_owner_id: string;
  provider: 'windsor'; action: WindsorReportAction; connector_id?: string; connector_account_id?: string;
  fields?: string[]; date_from?: string; date_to?: string; expected_context?: WindsorReportContext}
export interface WindsorReportResponse {ok: true; user_id: string; provider: 'windsor'; action: WindsorReportAction;
  read_contract: typeof WINDSOR_REPORT_CONTRACT; read_only: true; executed: false; windsor_report_context: WindsorReportContext;
  coverage: WindsorReportCoverage; sources?: WindsorReportSource[]; fields?: WindsorReportField[];
  connector_id?: string; connector_account_id?: string; date_filter_required?: boolean;
  rows?: Array<Record<string, WindsorScalar>>; date_from?: string; date_to?: string; queried_at?: string}
export const windsorObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
export const windsorUuid = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
export function windsorId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,299}$/.test(value) &&
    !['__proto__', 'constructor', 'prototype'].includes(value);
}
export function windsorDate(value: unknown): value is string {
  if (typeof value !== 'string' || value.startsWith('0000') || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const millis = Date.parse(value + 'T00:00:00Z');
  return Number.isFinite(millis) && new Date(millis).toISOString().slice(0, 10) === value;
}
export function isWindsorReportAction(value: unknown): value is WindsorReportAction {
  return typeof value === 'string' && (WINDSOR_REPORT_ACTIONS as readonly string[]).includes(value);
}
export function validateWindsorReportRequest(value: unknown, now = Date.now()): WindsorReportRequest {
  if (!windsorObject(value) || value.read_contract !== WINDSOR_REPORT_CONTRACT || value.provider !== 'windsor' ||
    !isWindsorReportAction(value.action) || !windsorUuid(value.expected_owner_id)) throw new CreatorError('invalid_request');
  const allowed = ['read_contract', 'expected_owner_id', 'provider', 'action',
    ...(value.action === 'report_sources' ? [] : ['connector_id', 'connector_account_id', 'expected_context']),
    ...(value.action === 'report' ? ['fields', 'date_from', 'date_to'] : [])];
  if (Object.keys(value).some(key => !allowed.includes(key))) throw new CreatorError('invalid_request');
  if (value.action !== 'report_sources' && !windsorReportContextValid(value.expected_context)) throw new CreatorError('invalid_report_context');
  if (value.action !== 'report_sources' && (!windsorId(value.connector_id) || !windsorId(value.connector_account_id))) {
    throw new CreatorError('invalid_report_selection');
  }
  if (value.action === 'report') {
    if (!Array.isArray(value.fields) || value.fields.length < 1 || value.fields.length > WINDSOR_REPORT_LIMITS.fields ||
      !value.fields.every(windsorId) || new Set(value.fields).size !== value.fields.length) throw new CreatorError('invalid_report_fields');
    if (!windsorDate(value.date_from) || !windsorDate(value.date_to) || value.date_from > value.date_to ||
      value.date_to > new Date(now).toISOString().slice(0, 10) ||
      (Date.parse(value.date_to) - Date.parse(value.date_from)) / 86400000 + 1 > WINDSOR_REPORT_LIMITS.days) {
      throw new CreatorError('invalid_report_dates');
    }
  }
  return value as unknown as WindsorReportRequest;
}

// Shared backend/client scalar rule. Missing values are protocol errors; literal null stays null.
export function windsorReportCellValid(value: unknown, type: WindsorFieldType): value is WindsorScalar {
  if (type === 'OBJECT') return false;
  if (value === null) return true;
  if (type === 'NUMERIC' || type === 'PERCENT') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'BOOLEAN') return typeof value === 'boolean';
  if (typeof value !== 'string' || value.length > WINDSOR_REPORT_LIMITS.cellChars) return false;
  if (type === 'DATE') return windsorDate(value);
  if (type === 'TIMESTAMP') {
    const match = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/u.exec(value);
    return !!match && windsorDate(value.slice(0, 10)) && Number(match[1]) < 24 &&
      Number(match[2]) < 60 && Number(match[3]) < 60 && Number.isFinite(Date.parse(value));
  }
  return type === 'TEXT';
}

// Names/descriptions remain inert text; multiline descriptions are legitimate metadata.
export function windsorReportMetadataTextValid(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.length <= max &&
    !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\p{Cf}\p{Cs}]/u.test(value);
}

export function windsorReportContextValid(value: unknown): value is WindsorReportContext {
  const keys = ['owner_id', 'session_id', 'provider', 'connection_id', 'generation', 'creator_account_id'];
  return windsorObject(value) && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key)) &&
    value.provider === 'windsor' && windsorUuid(value.owner_id) && windsorUuid(value.session_id) &&
    windsorUuid(value.connection_id) && windsorUuid(value.generation) &&
    windsorReportMetadataTextValid(value.creator_account_id, 300) && value.creator_account_id.length > 0;
}
